import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { I18nProvider, useT } from "@/i18n";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CenterProvider, useCenter } from "@/context/CenterContext";
import { RealtimeProvider } from "@/context/RealtimeContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import Employees from "@/pages/Employees";
import Schedule from "@/pages/Schedule";
import Tickets from "@/pages/Tickets";
import MyTickets from "@/pages/MyTickets";
import Presence from "@/pages/Presence";
import Chat from "@/pages/Chat";
import TimeOff from "@/pages/TimeOff";
import Availability from "@/pages/Availability";
import Centers from "@/pages/Centers";
import Residents from "@/pages/Residents";
import Care from "@/pages/Care";
import Training from "@/pages/Training";
import Calendar from "@/pages/Calendar";
import Billing from "@/pages/Billing";
import Platform from "@/pages/Platform";
import Settings from "@/pages/Settings";
import Inbox from "@/pages/Inbox";
import Assistant from "@/pages/Assistant";
import ShiftReports from "@/pages/ShiftReports";
import { HeartPulse, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-12 h-12 rounded-2xl bg-primary flex items-center justify-center animate-pulse">
        <HeartPulse className="w-6 h-6 text-white" />
      </div>
    </div>
  );
}

function NoCentersScreen() {
  const { isSuperAdmin } = useCenter();
  const { t } = useT();
  const { logout } = useAuth();
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-6 text-center">
      <Building2 className="w-12 h-12 text-muted-foreground mb-4" />
      <h2 className="font-display text-2xl font-bold mb-2">{isSuperAdmin ? "Create your first center" : "No center assigned"}</h2>
      <p className="text-muted-foreground max-w-md mb-6">
        {isSuperAdmin ? "You don't have any centers yet. Create one to get started." : "Ask your administrator to grant you access to a center."}
      </p>
      {isSuperAdmin
        ? <a href="/centers"><Button data-testid="create-first-center">Manage Centers</Button></a>
        : <Button variant="outline" onClick={logout}>{t("signOut")}</Button>}
    </div>
  );
}

function Shell() {
  const { centers, activeId, role, isSuperAdmin } = useCenter();
  if (centers === null) return <LoadingScreen />;
  if (centers.length === 0 && !isSuperAdmin) return <NoCentersScreen />;

  const isEmployee = role === "employee" && !isSuperAdmin;
  const home = isEmployee ? "/my-schedule" : "/";

  // super admin with no centers → route only to centers page
  if (centers.length === 0 && isSuperAdmin) {
    return (
      <RealtimeProvider>
        <Routes>
          <Route path="/centers" element={<Layout><Centers /></Layout>} />
          <Route path="*" element={<NoCentersScreen />} />
        </Routes>
      </RealtimeProvider>
    );
  }

  return (
    <RealtimeProvider>
      <Layout>
        <div key={activeId} className="h-full">
          <Routes>
            <Route path="/" element={isEmployee ? <Navigate to="/my-schedule" /> : <Dashboard />} />
            <Route path="/employees" element={isEmployee ? <Navigate to={home} /> : <Employees />} />
            <Route path="/residents" element={isEmployee ? <Navigate to={home} /> : <Residents />} />
            <Route path="/care" element={<Care />} />
            <Route path="/training" element={<Training />} />
            <Route path="/calendar" element={<Calendar />} />
            <Route path="/assistant" element={<Assistant />} />
            <Route path="/inbox" element={isEmployee ? <Navigate to={home} /> : <Inbox />} />
            <Route path="/billing" element={isEmployee ? <Navigate to={home} /> : <Billing />} />
            <Route path="/platform" element={isSuperAdmin ? <Platform /> : <Navigate to={home} />} />
            <Route path="/schedule" element={isEmployee ? <Navigate to={home} /> : <Schedule />} />
            <Route path="/my-schedule" element={<Schedule employeeMode />} />
            <Route path="/tickets" element={isEmployee ? <Navigate to={home} /> : <Tickets />} />
            <Route path="/my-tickets" element={<MyTickets />} />
            <Route path="/presence" element={isEmployee ? <Navigate to={home} /> : <Presence />} />
            <Route path="/chat" element={<Chat />} />
            <Route path="/shift-reports" element={isEmployee ? <Navigate to={home} /> : <ShiftReports />} />
            <Route path="/time-off" element={<TimeOff />} />
            <Route path="/availability" element={<Availability />} />
            <Route path="/centers" element={isSuperAdmin ? <Centers /> : <Navigate to={home} />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="*" element={<Navigate to={home} />} />
          </Routes>
        </div>
      </Layout>
    </RealtimeProvider>
  );
}

function Root() {
  const { user } = useAuth();
  if (user === null) return <LoadingScreen />;
  if (!user) return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
  return <CenterProvider><Shell /></CenterProvider>;
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <Root />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}
